import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LS_KEY = "istres.badges.unlocked";
const POLL_MS = 45000; // 45s

/**
 * Watcher silencieux qui détecte les nouveaux badges débloqués et
 * affiche un toast achievement + animation confetti.
 * - Refetch toutes les 45s + au retour de focus + sur l'événement custom 'istres-action-completed'
 * - Compare avec localStorage pour ne notifier QUE les vrais nouveaux unlocks
 */
export default function BadgeUnlockWatcher() {
  const { user } = useAuth();
  const [achievements, setAchievements] = useState([]); // queue de badges à afficher
  const knownRef = useRef(new Set());
  const initializedRef = useRef(false);

  // Chargement de l'état initial depuis localStorage
  useEffect(() => {
    if (!user) {
      knownRef.current = new Set();
      initializedRef.current = false;
      return;
    }
    try {
      const stored = JSON.parse(localStorage.getItem(`${LS_KEY}.${user.id || user.email}`) || "[]");
      knownRef.current = new Set(stored);
    } catch (_) {
      knownRef.current = new Set();
    }
  }, [user]);

  // Polling + déclenchements
  useEffect(() => {
    if (!user) return;
    let mounted = true;

    const check = async () => {
      try {
        const { data } = await api.get("/badges/mine");
        if (!mounted) return;
        const unlockedIds = data.badges.filter((b) => b.unlocked).map((b) => b.id);
        if (!initializedRef.current) {
          // 1er chargement : on synchronise sans notifier (sauf si localStorage existait)
          if (knownRef.current.size === 0) {
            knownRef.current = new Set(unlockedIds);
            localStorage.setItem(`${LS_KEY}.${user.id || user.email}`, JSON.stringify(unlockedIds));
          } else {
            // On a déjà un set local → détecte les nouveaux
            const fresh = unlockedIds.filter((id) => !knownRef.current.has(id));
            if (fresh.length > 0) {
              const meta = data.badges.filter((b) => fresh.includes(b.id));
              setAchievements((a) => [...a, ...meta]);
              knownRef.current = new Set(unlockedIds);
              localStorage.setItem(`${LS_KEY}.${user.id || user.email}`, JSON.stringify(unlockedIds));
            }
          }
          initializedRef.current = true;
        } else {
          const fresh = unlockedIds.filter((id) => !knownRef.current.has(id));
          if (fresh.length > 0) {
            const meta = data.badges.filter((b) => fresh.includes(b.id));
            setAchievements((a) => [...a, ...meta]);
            knownRef.current = new Set(unlockedIds);
            localStorage.setItem(`${LS_KEY}.${user.id || user.email}`, JSON.stringify(unlockedIds));
          }
        }
      } catch (_) { /* silent */ }
    };

    check();
    const id = setInterval(check, POLL_MS);
    const onFocus = () => check();
    const onAction = () => check();
    window.addEventListener("focus", onFocus);
    window.addEventListener("istres-action-completed", onAction);

    return () => {
      mounted = false;
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("istres-action-completed", onAction);
    };
  }, [user]);

  const dismiss = (idx) => {
    setAchievements((a) => a.filter((_, i) => i !== idx));
  };

  if (!user || achievements.length === 0) return null;

  // On n'affiche qu'un toast à la fois (FIFO), pas un stack
  const current = achievements[0];

  return (
    <>
      <AchievementToast badge={current} onDismiss={() => dismiss(0)} />
      <Confetti key={current.id} />
    </>
  );
}

/* ============================================================
 * Achievement toast
 * ============================================================ */
function AchievementToast({ badge, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 6000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  const tierBg =
    badge.tier === "gold"
      ? "from-sun-300 via-pink-300 to-pink-500"
      : badge.tier === "silver"
      ? "from-cream-100 via-cream-200 to-plum-300"
      : "from-amber-200 via-amber-300 to-amber-500";

  return (
    <div
      className="fixed top-6 left-1/2 -translate-x-1/2 z-[80] sm:top-8 animate-achievement"
      data-testid="achievement-toast"
      role="status"
      aria-live="polite"
    >
      <div className={`relative flex items-center gap-3 px-5 py-3.5 rounded-3xl bg-gradient-to-r ${tierBg} shadow-2xl shadow-pink-500/40 max-w-md`}>
        <div className="w-14 h-14 rounded-2xl bg-white grid place-items-center shrink-0 shadow-lg text-3xl animate-badge-pop">
          {badge.emoji}
        </div>
        <div className="min-w-0">
          <div className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-plum-900/75">
            🏆 Badge débloqué !
          </div>
          <div className="font-display font-extrabold text-[18px] text-plum-900 leading-tight" data-testid="achievement-title">
            {badge.label}
          </div>
          <div className="text-[12px] text-plum-900/75 line-clamp-1">{badge.description}</div>
        </div>
        <button
          onClick={onDismiss}
          className="ml-2 shrink-0 w-7 h-7 rounded-full bg-white/40 hover:bg-white/60 text-plum-900 grid place-items-center transition-colors"
          aria-label="Fermer"
          data-testid="achievement-dismiss"
        >
          ✕
        </button>
      </div>
      <style>{`
        @keyframes achievement-in {
          0%   { opacity: 0; transform: translate(-50%, -50px) scale(0.85); }
          15%  { opacity: 1; transform: translate(-50%, 8px) scale(1.05); }
          25%  { transform: translate(-50%, 0) scale(1); }
          85%  { opacity: 1; transform: translate(-50%, 0) scale(1); }
          100% { opacity: 0; transform: translate(-50%, -20px) scale(0.95); }
        }
        .animate-achievement {
          animation: achievement-in 6s ease-out forwards;
        }
        @keyframes badge-pop {
          0%   { transform: scale(0) rotate(-180deg); }
          70%  { transform: scale(1.15) rotate(10deg); }
          100% { transform: scale(1) rotate(0deg); }
        }
        .animate-badge-pop {
          animation: badge-pop 0.7s cubic-bezier(0.18, 0.89, 0.32, 1.28) both;
        }
      `}</style>
    </div>
  );
}

/* ============================================================
 * Confetti — DIY 50 particules CSS-only (pas de dépendance ajoutée)
 * ============================================================ */
function Confetti() {
  const colors = ["#F4B860", "#E66C7A", "#7A4F94", "#5BA88D", "#F8E5A1", "#F2C8D2"];
  const pieces = Array.from({ length: 60 }).map((_, i) => {
    const left = Math.random() * 100;
    const delay = Math.random() * 0.6;
    const duration = 1.8 + Math.random() * 1.4;
    const color = colors[i % colors.length];
    const rot = Math.random() * 360;
    const size = 6 + Math.random() * 8;
    const horizDrift = (Math.random() - 0.5) * 200;
    return { id: i, left, delay, duration, color, rot, size, horizDrift };
  });

  return (
    <div className="fixed inset-0 z-[79] pointer-events-none overflow-hidden" aria-hidden="true">
      {pieces.map((p) => (
        <span
          key={p.id}
          className="absolute confetti-piece"
          style={{
            left: `${p.left}%`,
            top: "-10%",
            width: `${p.size}px`,
            height: `${p.size * 0.4}px`,
            background: p.color,
            transform: `rotate(${p.rot}deg)`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            "--drift": `${p.horizDrift}px`,
          }}
        />
      ))}
      <style>{`
        @keyframes confetti-fall {
          0%   { transform: translate(0, 0) rotate(0deg); opacity: 1; }
          100% { transform: translate(var(--drift, 0), 110vh) rotate(720deg); opacity: 0.4; }
        }
        .confetti-piece {
          border-radius: 1px;
          animation-name: confetti-fall;
          animation-timing-function: cubic-bezier(0.18, 0.89, 0.32, 1.28);
          animation-fill-mode: forwards;
        }
        @media (prefers-reduced-motion: reduce) {
          .confetti-piece { display: none; }
        }
      `}</style>
    </div>
  );
}
